import { useTenant } from "@/providers/session";
import { useGetCompany } from "@/queries/companies/fetch";
import { useUpdateCompany } from "@/queries/companies/update";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";

const CREDITOR_START_OPTIONS = ["7", "9"];

export default function Configuration() {
  const { t } = useTranslation();
  const { tenantId } = useTenant();

  const { companyData, refetchCompany } = useGetCompany();
  const { updateCompany } = useUpdateCompany();

  const noGeneratedBookingTextInExportPayables =
    companyData?.company?.noGeneratedBookingTextInExportPayables ?? false;
  const noGeneratedBookingTextInExportReceivables =
    companyData?.company?.noGeneratedBookingTextInExportReceivables ?? false;
  const useCustomerNumberAsDebtorAccount =
    companyData?.company?.useCustomerNumberAsDebtorAccount ?? false;
  const creditorAccountStart =
    companyData?.company?.creditorAccountStart ?? "7";

  const handleUpdate = async (field: string, value: string | boolean) => {
    try {
      await updateCompany({
        variables: {
          companyId: tenantId,
          updatedFields: { [field]: value },
        },
      });
      await refetchCompany();
      toast.success(t("configuration.save_success"));
    } catch (error) {
      console.error(`Failed to update ${field}:`, error);
      toast.error(t("configuration.save_error"));
    }
  };

  const showCustomCreditorOption =
    creditorAccountStart &&
    !CREDITOR_START_OPTIONS.includes(creditorAccountStart);

  return (
    <Card>
      <CardContent>
        <div className="space-y-0">
          <div className="flex flex-col lg:flex-row lg:items-center py-4 gap-4">
            <div>
              <Label>{t("configuration.booking_text_payables.label")}</Label>
              <p className="text-xs text-gray-600 mt-1">
                {t("configuration.booking_text_payables.description")}
              </p>
            </div>
            <div className="shrink-0 ml-auto">
              <Select
                value={String(noGeneratedBookingTextInExportPayables)}
                onValueChange={(v) =>
                  handleUpdate(
                    "noGeneratedBookingTextInExportPayables",
                    v === "true",
                  )
                }
              >
                <SelectTrigger className="w-60">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="false">
                    {t("configuration.booking_text_payables.with_booking_text")}
                  </SelectItem>
                  <SelectItem value="true">
                    {t(
                      "configuration.booking_text_payables.without_booking_text",
                    )}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Separator />

          <div className="flex flex-col lg:flex-row lg:items-center py-4 gap-4">
            <div>
              <Label>{t("configuration.booking_text_receivables.label")}</Label>
              <p className="text-xs text-gray-600 mt-1">
                {t("configuration.booking_text_receivables.description")}
              </p>
            </div>
            <div className="shrink-0 ml-auto">
              <Select
                value={String(noGeneratedBookingTextInExportReceivables)}
                onValueChange={(v) =>
                  handleUpdate(
                    "noGeneratedBookingTextInExportReceivables",
                    v === "true",
                  )
                }
              >
                <SelectTrigger className="w-60">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="false">
                    {t(
                      "configuration.booking_text_receivables.with_booking_text",
                    )}
                  </SelectItem>
                  <SelectItem value="true">
                    {t(
                      "configuration.booking_text_receivables.without_booking_text",
                    )}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Separator />

          <div className="flex flex-col lg:flex-row lg:items-center py-4 gap-4">
            <div>
              <Label>{t("configuration.debtor_numbering.label")}</Label>
              <p className="text-xs text-gray-600 mt-1">
                {t("configuration.debtor_numbering.description")}
              </p>
            </div>
            <div className="shrink-0 ml-auto">
              <Select
                value={String(useCustomerNumberAsDebtorAccount)}
                onValueChange={(v) =>
                  handleUpdate("useCustomerNumberAsDebtorAccount", v === "true")
                }
              >
                <SelectTrigger className="w-60">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="false">
                    {t("configuration.debtor_numbering.next_free")}
                  </SelectItem>
                  <SelectItem value="true">
                    {t("configuration.debtor_numbering.customer_number")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Separator />

          <div className="flex flex-col lg:flex-row lg:items-center py-4 gap-4">
            <div>
              <Label>{t("configuration.creditor_range.label")}</Label>
              <p className="text-xs text-gray-600 mt-1">
                {t("configuration.creditor_range.description")}
              </p>
            </div>
            <div className="shrink-0 ml-auto">
              <Select
                value={creditorAccountStart}
                onValueChange={(v) => handleUpdate("creditorAccountStart", v)}
              >
                <SelectTrigger className="w-60">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CREDITOR_START_OPTIONS.map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {t("configuration.creditor_range.option", { range: opt })}
                    </SelectItem>
                  ))}
                  {showCustomCreditorOption && (
                    <SelectItem value={creditorAccountStart}>
                      {creditorAccountStart}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
